import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";

const formatHours = (value = 0) => {
  const totalHours = Number(value || 0);

  if (!Number.isFinite(totalHours)) {
    return "0.00";
  }

  const hours = Math.floor(totalHours);
  const minutes = Math.round((totalHours - hours) * 60);

  if (minutes === 60) {
    return `${hours + 1}.00`;
  }

  return `${hours}.${String(minutes).padStart(2, "0")}`;
};

const getCurrentMonthValue = () => {
  const today = new Date();

  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    "0",
  )}`;
};

const getCurrentYear = () => String(new Date().getFullYear());

const getIsoWeekInfo = (date = new Date()) => {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const year = d.getUTCFullYear();
  const yearStart = new Date(Date.UTC(year, 0, 1));
  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return { year, week };
};

const getCurrentWeekValue = () => {
  const { year, week } = getIsoWeekInfo();
  return `${year}-W${String(week).padStart(2, "0")}`;
};

const formatDateForApi = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const getWeekDateRange = (weekValue) => {
  if (!weekValue) return { fromDate: "", toDate: "" };
  const [yearText, weekText] = weekValue.split("-W");
  const year = Number(yearText);
  const week = Number(weekText);
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(week) ||
    week < 1 ||
    week > 53
  ) {
    return { fromDate: "", toDate: "" };
  }
  const januaryFourth = new Date(year, 0, 4);
  const januaryFourthDay = januaryFourth.getDay() || 7;
  const firstMonday = new Date(januaryFourth);
  firstMonday.setDate(januaryFourth.getDate() - januaryFourthDay + 1);
  const weekStart = new Date(firstMonday);
  weekStart.setDate(firstMonday.getDate() + (week - 1) * 7);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  return {
    fromDate: formatDateForApi(weekStart),
    toDate: formatDateForApi(weekEnd),
  };
};

const ProjectHoursSummary = () => {
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  const [filterMode, setFilterMode] = useState("month");
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeekValue());
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthValue());
  const [selectedYear, setSelectedYear] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchAvailableYears = async () => {
    try {
      const response = await api.get("/logs/project-hours-years", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const years = Array.isArray(response.data?.years)
        ? response.data.years
            .map(Number)
            .filter(Number.isInteger)
            .sort((first, second) => second - first)
        : [];

      setAvailableYears(years);

      if (years.length > 0) {
        setSelectedYear(String(years[0]));
      } else {
        setSelectedYear("");
      }
    } catch (error) {
      console.error("Failed to fetch available project years:", error);

      setAvailableYears([]);
      setSelectedYear("");
    }
  };

  const fetchSummary = async (overrideParams = null) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const params = overrideParams || {};

      if (!overrideParams && filterMode === "week" && selectedWeek) {
        const weekRange = getWeekDateRange(selectedWeek);
        params.fromDate = weekRange.fromDate;
        params.toDate = weekRange.toDate;
      }

      if (!overrideParams && filterMode === "month" && selectedMonth) {
        const [year, month] = selectedMonth.split("-").map(Number);
        params.year = year;
        params.month = month;
      }

      if (!overrideParams && filterMode === "year" && selectedYear) {
        params.year = Number(selectedYear);
      }

      if (!overrideParams && filterMode === "range" && fromDate && toDate) {
        params.fromDate = fromDate;
        params.toDate = toDate;
      }

      const response = await api.get("/logs/project-hours-summary", {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const responseProjects = Array.isArray(response.data?.projects)
        ? response.data.projects
        : [];

      const responseEmployees = Array.isArray(response.data?.employees)
        ? response.data.employees
        : [];

      setProjects(responseProjects);
      setEmployees(responseEmployees);
    } catch (error) {
      console.error("Project hours summary error:", error);

      setProjects([]);
      setEmployees([]);

      setErrorMessage(
        error.response?.data?.message ||
          error.message ||
          "Failed to load project-wise employee hours summary.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailableYears();
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredProjects = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return projects;
    }

    return projects.filter((project) => {
      const projectName = String(project.projectName || "").toLowerCase();

      const category = String(project.category || "").toLowerCase();

      return (
        projectName.includes(normalizedSearch) ||
        category.includes(normalizedSearch)
      );
    });
  }, [projects, search]);

  const grandTotals = useMemo(() => {
    const employeeTotals = {};

    employees.forEach((employeeName) => {
      employeeTotals[employeeName] = 0;
    });

    let totalHours = 0;

    filteredProjects.forEach((project) => {
      totalHours += Number(project.totalHours || 0);

      employees.forEach((employeeName) => {
        employeeTotals[employeeName] += Number(
          project.employeeHours?.[employeeName] || 0,
        );
      });
    });

    return {
      totalHours,
      employeeTotals,
    };
  }, [filteredProjects, employees]);

  const groupedProjects = useMemo(() => {
    const groups = {};

    filteredProjects.forEach((project) => {
      const category = String(project.category || "").trim() || "Uncategorized";

      if (!groups[category]) {
        groups[category] = [];
      }

      groups[category].push(project);
    });

    Object.keys(groups).forEach((category) => {
      groups[category].sort((first, second) =>
        String(first.projectName || "").localeCompare(
          String(second.projectName || ""),
        ),
      );
    });

    return groups;
  }, [filteredProjects]);

  const handleApplyFilter = () => {
    if (filterMode === "week" && !selectedWeek) {
      alert("Please select a week.");
      return;
    }
    if (filterMode === "month" && !selectedMonth) {
      alert("Please select a month.");
      return;
    }
    if (filterMode === "year" && !selectedYear) {
      alert("Please select a year.");
      return;
    }
    if (filterMode === "range" && (!fromDate || !toDate)) {
      alert("Please select both From Date and To Date.");
      return;
    }
    if (filterMode === "range" && new Date(fromDate) > new Date(toDate)) {
      alert("From Date cannot be after To Date.");
      return;
    }
    fetchSummary();
  };

  const handleReset = () => {
    setFilterMode("month");
    setSelectedWeek(getCurrentWeekValue());
    setSelectedMonth(getCurrentMonthValue());
    setSelectedYear(availableYears.length > 0 ? String(availableYears[0]) : "");
    setFromDate("");
    setToDate("");
    setSearch("");

    setTimeout(() => {
      const [year, month] = getCurrentMonthValue().split("-").map(Number);
      fetchSummary({ year, month });
    }, 0);
  };

  const handleExportCsv = () => {
    if (!filteredProjects.length) {
      alert("No summary data available to export.");
      return;
    }

    const headers = ["Category", "Project Name", "Total Hours", ...employees];

    const rows = filteredProjects.map((project) => [
      project.category || "Uncategorized",
      project.projectName || "Unnamed Project",
      formatHours(project.totalHours),
      ...employees.map((employeeName) =>
        formatHours(project.employeeHours?.[employeeName] || 0),
      ),
    ]);

    rows.push([
      "",
      "Grand Total",
      formatHours(grandTotals.totalHours),
      ...employees.map((employeeName) =>
        formatHours(grandTotals.employeeTotals[employeeName] || 0),
      ),
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => {
            const safeValue = String(cell ?? "").replace(/"/g, '""');

            return `"${safeValue}"`;
          })
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "Project_Wise_Employee_Hours_Summary.csv";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📊 Project-wise Employee Hours Summary</h1>

          <p style={styles.subtitle}>
            View weekly, monthly, yearly, or custom date-range employee hours
            for every project.
          </p>
        </div>

        <button
          type="button"
          onClick={handleExportCsv}
          style={styles.exportButton}
        >
          ⬇ Export CSV
        </button>
      </div>

      <div style={styles.filterCard}>
        <div style={styles.filterModeGroup}>
          {[
            ["week", "Weekly"],
            ["month", "Monthly"],
            ["year", "Yearly"],
            ["range", "Date Range"],
          ].map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => setFilterMode(mode)}
              style={{
                ...styles.modeButton,
                ...(filterMode === mode ? styles.modeButtonActive : {}),
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div style={styles.filters}>
          {filterMode === "week" && (
            <div style={styles.field}>
              <label style={styles.label}>Select Week</label>
              <input
                type="week"
                value={selectedWeek}
                onChange={(event) => setSelectedWeek(event.target.value)}
                style={styles.input}
              />
            </div>
          )}

          {filterMode === "month" && (
            <div style={styles.field}>
              <label style={styles.label}>Select Month</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(event.target.value)}
                style={styles.input}
              />
            </div>
          )}

          {filterMode === "year" && (
            <div style={styles.field}>
              <label style={styles.label}>Select Year</label>
              <select
                value={selectedYear}
                onChange={(event) => setSelectedYear(event.target.value)}
                style={styles.input}
              >
                {availableYears.length === 0 ? (
                  <option value="">No years available</option>
                ) : (
                  availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))
                )}
              </select>
            </div>
          )}

          {filterMode === "range" && (
            <>
              <div style={styles.field}>
                <label style={styles.label}>From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                  style={styles.input}
                />
              </div>
            </>
          )}

          <div style={styles.field}>
            <label style={styles.label}>Search Project</label>

            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by project or category"
              style={styles.searchInput}
            />
          </div>

          <div style={styles.actionButtons}>
            <button
              type="button"
              onClick={handleApplyFilter}
              style={styles.applyButton}
            >
              Apply
            </button>

            <button
              type="button"
              onClick={handleReset}
              style={styles.resetButton}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {errorMessage && <div style={styles.error}>{errorMessage}</div>}

      <div style={styles.summaryCards}>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Total Projects</span>

          <strong style={styles.summaryValue}>{filteredProjects.length}</strong>
        </div>

        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Total Employees</span>

          <strong style={styles.summaryValue}>{employees.length}</strong>
        </div>

        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Total Hours</span>

          <strong style={styles.summaryValue}>
            {formatHours(grandTotals.totalHours)}
          </strong>
        </div>
      </div>

      {loading ? (
        <div style={styles.messageCard}>Loading project summary...</div>
      ) : filteredProjects.length === 0 ? (
        <div style={styles.messageCard}>
          No project hours found for the selected period.
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.stickyProjectHeader}>Project</th>

                <th style={styles.headerCell}>Total Hours</th>

                {employees.map((employeeName) => (
                  <th
                    key={employeeName}
                    style={styles.headerCell}
                    title={employeeName}
                  >
                    {employeeName}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {Object.entries(groupedProjects).map(
                ([category, categoryProjects]) => (
                  <React.Fragment key={category}>
                    <tr>
                      <td
                        colSpan={employees.length + 2}
                        style={styles.categoryRow}
                      >
                        {category.toUpperCase()}
                      </td>
                    </tr>

                    {categoryProjects.map((project) => (
                      <tr key={`${category}-${project.projectName}`}>
                        <td style={styles.stickyProjectCell}>
                          {project.projectName || "Unnamed Project"}
                        </td>

                        <td style={styles.totalCell}>
                          {formatHours(project.totalHours)}
                        </td>

                        {employees.map((employeeName) => {
                          const hours =
                            project.employeeHours?.[employeeName] || 0;

                          return (
                            <td
                              key={`${project.projectName}-${employeeName}`}
                              style={
                                hours > 0
                                  ? styles.hoursCellActive
                                  : styles.hoursCell
                              }
                            >
                              {hours > 0 ? formatHours(hours) : "-"}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ),
              )}

              <tr style={styles.grandTotalRow}>
                <td style={styles.stickyGrandTotalCell}>Grand Total</td>

                <td style={styles.grandTotalCell}>
                  {formatHours(grandTotals.totalHours)}
                </td>

                {employees.map((employeeName) => (
                  <td
                    key={`total-${employeeName}`}
                    style={styles.grandTotalCell}
                  >
                    {formatHours(grandTotals.employeeTotals[employeeName] || 0)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    padding: 20,
    background: "#f4f6fb",
    color: "#172033",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 18,
  },

  title: {
    margin: 0,
    fontSize: 26,
    fontWeight: 800,
    color: "#252b61",
  },

  subtitle: {
    margin: "6px 0 0",
    color: "#667085",
    fontSize: 14,
  },

  exportButton: {
    border: "1px solid #343a76",
    background: "#343a76",
    color: "#ffffff",
    padding: "10px 16px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 700,
  },

  filterCard: {
    background: "#ffffff",
    border: "1px solid #e4e7ec",
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    boxShadow: "0 4px 14px rgba(16,24,40,0.05)",
  },

  filterModeGroup: {
    display: "flex",
    gap: 8,
    marginBottom: 14,
    flexWrap: "wrap",
  },

  modeButton: {
    padding: "8px 14px",
    borderRadius: 7,
    border: "1px solid #d0d5dd",
    background: "#ffffff",
    cursor: "pointer",
    fontWeight: 700,
    color: "#344054",
  },

  modeButtonActive: {
    background: "#343a76",
    color: "#ffffff",
    borderColor: "#343a76",
  },

  filters: {
    display: "flex",
    alignItems: "flex-end",
    gap: 12,
    flexWrap: "wrap",
  },

  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  label: {
    fontSize: 12,
    fontWeight: 700,
    color: "#475467",
  },

  input: {
    minWidth: 175,
    padding: "9px 11px",
    border: "1px solid #d0d5dd",
    borderRadius: 7,
    outline: "none",
    background: "#ffffff",
  },

  searchInput: {
    minWidth: 260,
    padding: "9px 11px",
    border: "1px solid #d0d5dd",
    borderRadius: 7,
    outline: "none",
    background: "#ffffff",
  },

  actionButtons: {
    display: "flex",
    gap: 8,
  },

  applyButton: {
    border: "none",
    background: "#2563eb",
    color: "#ffffff",
    padding: "10px 16px",
    borderRadius: 7,
    cursor: "pointer",
    fontWeight: 700,
  },

  resetButton: {
    border: "1px solid #d0d5dd",
    background: "#ffffff",
    color: "#344054",
    padding: "9px 16px",
    borderRadius: 7,
    cursor: "pointer",
    fontWeight: 700,
  },

  error: {
    background: "#fef3f2",
    border: "1px solid #fecdca",
    color: "#b42318",
    padding: "11px 14px",
    borderRadius: 8,
    marginBottom: 14,
    fontSize: 13,
  },

  summaryCards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 16,
  },

  summaryCard: {
    background: "#ffffff",
    border: "1px solid #e4e7ec",
    borderRadius: 10,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  summaryLabel: {
    color: "#667085",
    fontSize: 13,
    fontWeight: 600,
  },

  summaryValue: {
    color: "#252b61",
    fontSize: 24,
    fontWeight: 800,
  },

  messageCard: {
    padding: 30,
    background: "#ffffff",
    border: "1px dashed #d0d5dd",
    borderRadius: 10,
    textAlign: "center",
    color: "#667085",
  },

  tableWrapper: {
    overflow: "auto",
    maxHeight: "calc(100vh - 310px)",
    background: "#ffffff",
    border: "1px solid #e4e7ec",
    borderRadius: 12,
    boxShadow: "0 4px 16px rgba(16,24,40,0.06)",
  },

  table: {
    width: "100%",
    minWidth: 900,
    borderCollapse: "separate",
    borderSpacing: 0,
  },

  headerCell: {
    position: "sticky",
    top: 0,
    zIndex: 3,
    background: "#343a76",
    color: "#ffffff",
    padding: 11,
    borderRight: "1px solid rgba(255,255,255,0.14)",
    whiteSpace: "nowrap",
    textAlign: "center",
    fontSize: 13,
  },

  stickyProjectHeader: {
    position: "sticky",
    top: 0,
    left: 0,
    zIndex: 5,
    minWidth: 260,
    background: "#343a76",
    color: "#ffffff",
    padding: 11,
    textAlign: "left",
    whiteSpace: "nowrap",
  },

  categoryRow: {
    background: "#e9ecf8",
    color: "#252b61",
    padding: "10px 12px",
    fontWeight: 800,
    borderBottom: "1px solid #d5d9eb",
  },

  stickyProjectCell: {
    position: "sticky",
    left: 0,
    zIndex: 2,
    minWidth: 260,
    maxWidth: 260,
    background: "#ffffff",
    padding: "10px 12px",
    borderBottom: "1px solid #eaecf0",
    borderRight: "1px solid #eaecf0",
    fontWeight: 600,
    textAlign: "left",
    wordBreak: "break-word",
  },

  totalCell: {
    padding: 10,
    borderBottom: "1px solid #eaecf0",
    borderRight: "1px solid #eaecf0",
    textAlign: "center",
    fontWeight: 800,
    color: "#252b61",
    whiteSpace: "nowrap",
  },

  hoursCell: {
    padding: 10,
    borderBottom: "1px solid #eaecf0",
    borderRight: "1px solid #eaecf0",
    textAlign: "center",
    color: "#98a2b3",
  },

  hoursCellActive: {
    padding: 10,
    borderBottom: "1px solid #eaecf0",
    borderRight: "1px solid #eaecf0",
    textAlign: "center",
    background: "#f0f4ff",
    color: "#1d4ed8",
    fontWeight: 700,
  },

  grandTotalRow: {
    background: "#e4e7ec",
  },

  stickyGrandTotalCell: {
    position: "sticky",
    left: 0,
    zIndex: 2,
    background: "#343a76",
    color: "#ffffff",
    padding: "11px 12px",
    fontWeight: 800,
    borderRight: "1px solid #ffffff33",
  },

  grandTotalCell: {
    background: "#343a76",
    color: "#ffffff",
    padding: 11,
    fontWeight: 800,
    textAlign: "center",
    whiteSpace: "nowrap",
  },
};

export default ProjectHoursSummary;
